// src/config.js
// Simple configuration file for shared constants and settings

// Improved Authentication config with secure token-based auth
export const AUTH_CONFIG = {
  // Check if user is authenticated by verifying token
  isAuthenticated: async () => {
    try {
      console.log('AUTH_CONFIG.isAuthenticated called');
      const token = localStorage.getItem('authToken');
      
      // If no token exists, user is not authenticated
      if (!token) {
        console.log('No auth token found, user is not authenticated');
        return false;
      }
      
      try {
        // First try a simple validation locally - this prevents unnecessary server calls
        const tokenData = JSON.parse(atob(token));
        if (tokenData.exp < Date.now()) {
          console.log('Token expired locally');
          localStorage.removeItem('authToken');
          localStorage.removeItem('authTokenExpiry');
          return false;
        }
        console.log('Local token validation passed');
      } catch (parseError) {
        console.error('Token parse error:', parseError);
        // If we can't parse the token, it's invalid
        localStorage.removeItem('authToken');
        localStorage.removeItem('authTokenExpiry');
        return false;
      }
      
      // To avoid the token verification flashing login screen on admin page changes,
      // implement a debounced/cached server verification approach
      const cacheKey = 'lastTokenVerificationTime';
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes in milliseconds
      const lastVerified = parseInt(sessionStorage.getItem(cacheKey) || '0', 10);
      const now = Date.now();
      
      // Only verify with server if cache expired or doesn't exist
      if (now - lastVerified > cacheExpiry) {
        console.log('Token verification cache expired, verifying with server');
        
        try {
          // Verify token with server
          console.log('Verifying token with server');
          const response = await fetch('/api/admin/auth', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'verify',
              token
            })
          });
          
          // If server is down, fallback to local validation
          if (!response.ok) {
            console.warn('Server auth validation failed, using local validation as fallback');
            console.log('USING FALLBACK AUTH - ADMIN ACCESS GRANTED');
            // Update cache timestamp even for fallback
            sessionStorage.setItem(cacheKey, now.toString());
            return true; // We already validated locally above
          }
          
          const data = await response.json();
          console.log('Server auth validation response:', data);
          
          // If token expired or invalid, clear it
          if (!data.valid || data.expired) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('authTokenExpiry');
            sessionStorage.removeItem(cacheKey);
            return false;
          }
          
          // Update cache timestamp
          sessionStorage.setItem(cacheKey, now.toString());
          console.log('Authentication successful, admin access granted');
          return true;
        } catch (fetchError) {
          console.error('Error fetching auth verification:', fetchError);
          // If server is down, use local validation as fallback
          console.log('ERROR DURING AUTH - USING FALLBACK - ADMIN ACCESS GRANTED');
          // Update cache timestamp even for fallback
          sessionStorage.setItem(cacheKey, now.toString());
          return true; // We already validated locally above
        }
      } else {
        // Use cached validation result
        console.log('Using cached token verification, last verified:', new Date(lastVerified).toLocaleTimeString());
        return true;
      }
    } catch (error) {
      console.error('Error verifying authentication:', error);
      return false;
    }
  },
  
  // Get auth token
  getToken: () => localStorage.getItem('authToken'),
  
  // Login with username and password
  login: async (username, password) => {
    try {
      console.log('Auth login called with username:', username);
      
      const response = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'login',
          username,
          password
        })
      });
      
      console.log('Auth API response status:', response.status);
      
      // If the response is not ok, log more details
      if (!response.ok) {
        console.error('Auth API response error:', response.status, response.statusText);
      }
      
      const data = await response.json();
      console.log('Auth API response data:', data);
      
      if (data.success && data.token) {
        console.log('Login successful, storing token');
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authTokenExpiry', data.expiresAt);
        return true;
      }
      
      console.log('Login failed:', data.error || 'Unknown reason');
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  },
  
  // Logout by clearing stored tokens
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenExpiry');
  },
  
  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      console.log('Changing password...');
      
      // Get auth token
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        return { success: false, error: 'Not authenticated. Please log in again.' };
      }
      
      // Make the request to change password
      console.log('Sending password change request to server...');
      const response = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'changePassword',
          token,
          password: currentPassword,
          newPassword
        })
      });
      
      console.log('Password change response status:', response.status);
      const data = await response.json();
      console.log('Password change response:', data);
      
      if (data.expired) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authTokenExpiry');
        return { success: false, error: 'Authentication expired. Please log in again.' };
      }
      
      if (data.success) {
        // If successful, update localStorage with new token
        if (data.token) {
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('authTokenExpiry', data.expiresAt);
        }
        
        // Include warning message if password wasn't persisted
        console.log('Password changed successfully');
        console.log('Password persistence status:', data.passwordPersisted);
        
        if (data.warningMessage) {
          console.warn(data.warningMessage);
        }
      }
      
      return { 
        success: data.success, 
        error: data.error,
        warningMessage: data.warningMessage,
        passwordPersisted: data.passwordPersisted
      };
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, error: error.message };
    }
  }
};

// API service for quotes
export const quoteService = {
  getAllQuotes: async () => {
    const response = await fetch('/api/admin/quotes');
    if (!response.ok) {
      throw new Error('Failed to fetch quotes');
    }
    return response.json();
  },
  
  getQuote: async (id) => {
    const response = await fetch(`/api/admin/quotes/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch quote');
    }
    return response.json();
  },
  
  addQuote: async (quoteData) => {
    const response = await fetch('/api/admin/quotes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(quoteData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to add quote');
    }
    
    return response.json();
  },
  
  updateQuote: async (id, quoteData) => {
    const response = await fetch(`/api/admin/quotes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(quoteData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update quote');
    }
    
    return response.json();
  },
  
  deleteQuote: async (id) => {
    const response = await fetch(`/api/admin/quotes/${id}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete quote');
    }
    
    return response.json();
  },
  
  getAllTags: async () => {
    const response = await fetch('/api/admin/tags');
    if (!response.ok) {
      throw new Error('Failed to fetch tags');
    }
    return response.json();
  },
  
  updateTag: async (oldName, newName) => {
    const response = await fetch(`/api/admin/tags/${oldName}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ newName })
    });
    
    if (!response.ok) {
      throw new Error('Failed to update tag');
    }
    
    return response.json();
  },
  
  deleteTag: async (name) => {
    const response = await fetch(`/api/admin/tags/${name}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete tag');
    }
    
    return response.json();
  },
  
  mergeTags: async (sourceTag, targetTag) => {
    const response = await fetch('/api/admin/tags/merge', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sourceTag, targetTag })
    });
    
    if (!response.ok) {
      throw new Error('Failed to merge tags');
    }
    
    return response.json();
  }
};

// API configuration
export const API_CONFIG = {
  // Dynamic base URL detection - always use current origin to avoid CORS issues
  get baseUrl() {
    if (typeof window === 'undefined') return '';
    return '';  // Use relative URLs by default
  },
  getFullUrl: (path) => {
    // If path starts with a slash, keep it to use relative URLs
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // Return relative URL to use current domain
    return cleanPath;
  }
};

// Article service
export const articleService = {
  extractArticle: async (url) => {
    const response = await fetch(API_CONFIG.getFullUrl('/api/articles/extract'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ url })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `${errorData.error || 'Failed to extract article content'}: ${errorData.details || ''} 
        ${errorData.errorName ? `(${errorData.errorName})` : ''} 
        ${errorData.errorCode ? `Code: ${errorData.errorCode}` : ''}`
      );
    }
    
    return response.json();
  },
  
  submitArticle: async (articleData) => {
    const response = await fetch(API_CONFIG.getFullUrl('/api/articles/submit'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(articleData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit article');
    }
    
    return response.json();
  },
  
  updateArticle: async (id, articleData) => {
    const response = await fetch(API_CONFIG.getFullUrl(`/api/articles/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(articleData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update article');
    }
    
    return response.json();
  }
};

// Application configuration
export const APP_CONFIG = {
  appName: 'Quotes Simple',
  version: '1.0.0'
};